/**
 * v2.7.65+E126 · Emergency Action Drawer
 * ═════════════════════════════════════════════════════════════════════
 * Two BIG, always-visible tap targets on the driver dashboard:
 *
 *   1. CALL DISPATCH → native `tel:` link to (580) 219-4687
 *   2. CALL 911       → native `tel:911` link (uninterrupted)
 *
 * Uses `tel:` URLs so iOS / Android drop straight into the phone dialer
 * — no in-app calling infrastructure, no Twilio round-trips, zero cost.
 * The 911 tile is red / high-contrast so it's unmistakable in a
 * high-stress moment.
 */
import { Card } from "@/components/ui/card";
import { PhoneCall, Siren } from "lucide-react";

const DISPATCH_NUMBER_DISPLAY = "(580) 219-4687";
const DISPATCH_NUMBER_TEL     = "+15802194687";

export default function EmergencyActionDrawer() {
  return (
    <Card
      className="p-3 border-rose-200 dark:border-rose-900/40 bg-white dark:bg-zinc-950"
      data-testid="emergency-action-drawer"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Siren className="h-3.5 w-3.5 text-rose-500"/>
        <span className="text-[10px] uppercase tracking-widest font-bold text-rose-600 dark:text-rose-400">
          Emergency
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {/* Dispatch — amber */}
        <a
          href={`tel:${DISPATCH_NUMBER_TEL}`}
          data-testid="emergency-call-dispatch"
          className="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-900/20 py-3 px-2 hover:bg-amber-100 dark:hover:bg-amber-900/30 active:scale-[0.98] transition-all touch-manipulation"
          aria-label={`Call Dispatch at ${DISPATCH_NUMBER_DISPLAY}`}
        >
          <PhoneCall className="h-5 w-5 text-amber-600 dark:text-amber-400"/>
          <div className="text-[11px] uppercase tracking-widest font-black text-amber-900 dark:text-amber-200 leading-tight">
            Call Dispatch
          </div>
          <div className="text-[10px] font-mono text-amber-700 dark:text-amber-300">
            {DISPATCH_NUMBER_DISPLAY}
          </div>
        </a>
        {/* 911 — hard red */}
        <a
          href="tel:911"
          data-testid="emergency-call-911"
          className="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-rose-500 bg-rose-500 hover:bg-rose-600 active:scale-[0.98] transition-all py-3 px-2 touch-manipulation"
          aria-label="Call 911 Emergency Services"
        >
          <Siren className="h-5 w-5 text-white"/>
          <div className="text-[11px] uppercase tracking-widest font-black text-white leading-tight">
            Call 911
          </div>
          <div className="text-[10px] font-mono text-rose-100">
            Emergency
          </div>
        </a>
      </div>
    </Card>
  );
}
